// Account-scoped browser caches (LEA-298).
//
// A handful of `localStorage` keys cache data for the *currently logged-in
// account* (Dashboard Step 1 company profile, saved lead queries, scheduling
// drafts, manually added leads, the onboarding-dismissed flag). They use
// global keys with no `uid` baked in, so on a shared browser they would leak
// from one account to the next — e.g. log out of A → sign up as B → the
// dashboard's instant-paint seed still shows A's company until the background
// revalidate overwrites it.
//
// The fix: wipe these whenever the authenticated identity actually changes
// (logout, or switching to a different account). `reconcileAuthIdentity` is
// the single choke point — it is called from `auth.notify()` on every auth
// state change, and is a deliberate no-op for a same-account page reload so the
// instant-paint optimisation keeps working for normal navigation.
//
// Add every NEW per-account localStorage key to this list so the next dev has
// one obvious place to register it.

const ACCOUNT_SCOPED_KEYS = [
  'ls.userCompany', // Dashboard Step 1 company profile — the LEA-298 repro
  'cortex.leadQueries.v1', // saved lead-query map
  'ls.scheduledRuns.v1', // scheduling drafts
  'ls.manualLeads.v1', // manually added leads
  'ls.onboardingDismissed', // onboarding-gate dismissal flag
];

// Tracks the last authenticated uid we saw, so we can tell a same-account
// reload (no-op) from a real account switch (wipe). Persisted because the
// in-memory cached user is reset to null on every hard reload.
const LAST_AUTH_UID_KEY = 'ls.lastAuthUid';

/** Remove all account-scoped client caches. Safe if storage is unavailable. */
export function clearAccountScopedCaches(): void {
  try {
    for (const key of ACCOUNT_SCOPED_KEYS) localStorage.removeItem(key);
  } catch {
    /* storage disabled / quota — non-fatal */
  }
}

/**
 * Clear account-scoped caches iff the authenticated uid changed since the last
 * time we saw one. Idempotent and safe to call on every auth notification:
 *
 *  - same-account reload (prev === next) → no-op, instant-paint caches survive
 *  - first login on a fresh browser (prev === null) → no-op, nothing to leak
 *  - logout (next === null) or switch to a different uid → wipe
 */
export function reconcileAuthIdentity(uid: string | null): void {
  try {
    const prev = localStorage.getItem(LAST_AUTH_UID_KEY);
    const next = uid ?? '';
    if (prev !== null && prev !== next) {
      clearAccountScopedCaches();
    }
    if (next) localStorage.setItem(LAST_AUTH_UID_KEY, next);
    else localStorage.removeItem(LAST_AUTH_UID_KEY);
  } catch {
    /* storage disabled / quota — non-fatal */
  }
}
